import { __decorate, __metadata } from "tslib";
import { I18nThemed } from 'interfaces/mixins';
import { lccstore } from 'lcc-state';
import { logQuiz } from 'logger';
import { subscribe } from 'pub-sub';
import timesync from 'timesync';
import { beforeProperties } from '@dojo/framework/core/decorators/beforeProperties';
import { theme } from '@dojo/framework/core/mixins/Themed';
import { v, w } from '@dojo/framework/core/vdom';
import Button from '@dojo/widgets/button';
import Dialog from '@dojo/widgets/dialog';
import { formatAriaProperties } from '@dojo/widgets/common/util';
import css from '../css/discussion.m.css';
import svc from '../support/discussion-svc';
import { EssayItem, EssayPane } from '../widgets/essay-pane';
const ISSEEN = 0;
let Discussion = class Discussion extends I18nThemed {
    svc = svc;
    essayList = [];
    selectedEssay = undefined;
    selectedComments = [];
    handles = [];
    areyousure = false;
    _propsInited = false;
    data = '';
    _inSave = false;
    _inAysFinish = false;
    getData = () => this.data;
    onAttach() {
        const h1 = lccstore.onChange(lccstore.path('dynls'), () => this.invalidate());
        const h2 = subscribe('discussion:essay:add', (topic, msg) => {
            const { quizid } = this.properties;
            if (this.essayList.length > 0 && quizid === msg.quiz) {
                this.load_essaylist();
            }
        });
        this.handles.push(h1);
        this.handles.push(h2);
    }
    onDetach() {
        this.handles.forEach((h) => {
            h.destroy && h.destroy();
            h.remove && h.remove();
        });
    }
    ensure_nls(properties) {
        const { get, path } = lccstore;
        const dynls = get(path('dynls'));
        const bundle = dynls.get('quiz');
        if (!bundle) {
            properties.localizedBundle = { isPlaceholder: true, format: null, messages: {} };
        }
        else {
            properties.localizedBundle = this.localizeBundle(bundle);
        }
        return properties;
    }
    update_result(answer, postIt = false) {
        const { question, updater } = this.properties;
        const { icode = {}, status, cursor } = question;
        question.flags[ISSEEN] = true;
        const icodeAnswer = answer !== undefined ? answer : this.getData();
        const data = {
            answer: icodeAnswer,
            icode: { ...icode, _answer: icodeAnswer },
            starttime: question.starttime,
            stoptime: timesync.now().toJSON(),
            isseen: true,
            ismarked: question.marked ? true : false
        };
        const qrlocator = {
            status: postIt ? 'posted' : status,
            cursor,
            qrid: '',
            quiz: ''
        };
        const result = { qrlocator, data };
        return updater([result]);
    }
    render_buttons() {
        const { theme, localizedBundle: { messages: { btnSave_close = 'Save and close', btnSubmit = 'Submit' } }, quizid } = this.properties;
        return [
            v('div', { xqa: 'action single', classes: [this.theme(css.oneaction)] }, [
                w(Button, {
                    theme,
                    key: 'save',
                    onClick: () => {
                        if (!this._inSave) {
                            this._inSave = true;
                            this.update_result().then(() => {
                                window.history.back();
                            });
                            logQuiz.info(() => `save and close ${quizid}`);
                        }
                    }
                }, [btnSave_close]),
                w(Button, {
                    theme,
                    key: 'submit',
                    onClick: () => {
                        this.areyousure = true;
                        this.invalidate();
                    }
                }, [btnSubmit])
            ])
        ];
    }
    async select_essay(essay) {
        const { progressionSvc } = this.properties;
        this.svc._smd.url = progressionSvc._smd.url;
        this.selectedEssay = essay;
        this.selectedComments = [];
        this.invalidate();
        this.selectedComments = await this.svc.get_replies({ resultid: `${essay.qrid}` });
        this.invalidate();
    }
    async reload_comments() {
        const essay = this.selectedEssay;
        this.selectedComments = await this.svc.get_replies({ resultid: `${essay.qrid}` });
        this.invalidate();
    }
    render_essaypane() {
        const { selectedEssay, selectedComments: comments, svc } = this;
        if (selectedEssay === undefined) {
            return v('div', { key: 'essay-pane' });
        }
        return v('div', { key: 'essay-pane', xqa: 'essay selected', classes: [this.theme(css.selectedEssay)] }, [
            w(EssayPane, {
                essay: selectedEssay,
                comments,
                svc,
                reloadComments: () => {
                    this.reload_comments();
                }
            })
        ]);
    }
    async load_essaylist() {
        const { progressionSvc } = this.properties;
        this.svc._smd.url = progressionSvc._smd.url;
        const essays = await this.svc.get_essays({});
        this.essayList = essays;
        if (essays.length > 0) {
            this.invalidate();
        }
    }
    render_essaylist() {
        const { essayList, selectedEssay } = this;
        if (essayList.length === 0) {
            this.load_essaylist();
        }
        const { question, localizedBundle: { messages: { discAllResponses } } } = this.properties;
        const { id } = question;
        return v('div', { key: id, xqa: `discussion ${id}`, classes: [this.theme(css.responseRoot)] }, [
            v('ul', { xqa: `essay list`, classes: [this.theme(css.essayList)] }, [
                v('li', { key: 'listhead', xqa: 'list head', classes: [this.theme(css.essayListHead)] }, [
                    discAllResponses
                ]),
                ...essayList.map((essay, idx) => {
                    return w(EssayItem, {
                        key: `essay-${idx}`,
                        essay,
                        isSelected: selectedEssay !== undefined ? essay.qrid === selectedEssay.qrid : false,
                        selectItem: (essay) => {
                            this.select_essay(essay);
                        }
                    });
                })
            ]),
            this.render_essaypane()
        ]);
    }
    areYouSure() {
        const { onFinish, quizid, localizedBundle: { messages: { ays_title: title = '', ays_body = '', ays_cancel = '', ays_submit = '' } } } = this.properties;
        return w(Dialog, { open: this.areyousure, onRequestClose: () => (this.areyousure = false) }, [
            {
                title,
                content: v('div', { classes: [this.theme(css.areYouSure)] }, [
                    v('div', { key: '1', classes: [this.theme(css.sureBody)] }, [ays_body]),
                    v('div', { key: '2', classes: [this.theme(css.sureButtons)] }, [
                        w(Button, {
                            key: 'ays-finish',
                            onClick: () => {
                                if (!this._inAysFinish) {
                                    this._inAysFinish = true;
                                    this.update_result(undefined, true).then(() => {
                                        onFinish();
                                    });
                                    logQuiz.info(() => `submit ${quizid}`);
                                }
                            }
                        }, [ays_submit]),
                        w(Button, {
                            key: 'ays-cancel',
                            onClick: () => {
                                this.areyousure = false;
                                this.invalidate();
                            }
                        }, [ays_cancel])
                    ])
                ])
            }
        ]);
    }
    render_question() {
        const { isPlaceholder } = this.properties.localizedBundle;
        if (isPlaceholder) {
            return null;
        }
        const { question, localizedBundle: { messages: { discInstructions } } } = this.properties;
        const { id, answertext } = question;
        return v('div', { key: id, xqa: `discussion ${id}`, classes: [this.theme(css.root)] }, [
            v('div', {
                key: 'question-text',
                id: 'quiz-instructions',
                classes: [this.theme(css.question)],
                innerHTML: discInstructions
            }),
            w('Editor', {
                content: answertext,
                onContent: (content) => {
                    this.data = content;
                }
            }),
            v('div', { key: 'action-block', xqa: 'action block', classes: [this.theme(css.actions)] }, [
                v('div', {
                    key: 'submit-action',
                    xqa: 'action submit',
                    classes: [this.theme(css.oneaction)],
                    role: 'group',
                    ...formatAriaProperties({ labelledby: 'quiz-instructions' })
                }, this.render_buttons())
            ]),
            this.areYouSure()
        ]);
    }
    render() {
        const { isPlaceholder } = this.properties.localizedBundle;
        if (isPlaceholder) {
            return null;
        }
        const { question, info } = this.properties;
        if (!question || !info) {
            return null;
        }
        const { start_state } = info;
        switch (start_state) {
            case 'question':
                return this.render_question();
            case 'posted':
            case 'graded':
                return this.render_essaylist();
            default:
                return null;
        }
    }
    propsInit(paths, store) {
        if (!this._propsInited) {
            const invalidatePaths = paths.map((path) => store.path(path.join('/')));
            const handle = store.onChange(invalidatePaths, () => this.invalidate());
            this.own({
                destroy: () => {
                    handle.remove();
                }
            });
            const h2 = lccstore.onChange(lccstore.path('dynls'), () => this.invalidate());
            this.own({
                destroy: () => {
                    h2.remove();
                }
            });
            this._propsInited = true;
        }
    }
    stateProperties(properties) {
        const { quizstore } = properties;
        const { get, path } = quizstore;
        this.propsInit([['question'], ['quiz'], ['loaded'], ['info']], quizstore);
        const quizdata = get(path('quiz'));
        properties.info = get(path('info'));
        properties.question = get(path('question'));
        properties.quizkind = quizdata.kind;
        properties.quizid = quizdata.id;
        properties.loaded = get(path('loaded'));
        return properties;
    }
};
__decorate([
    beforeProperties(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", Object)
], Discussion.prototype, "ensure_nls", null);
__decorate([
    beforeProperties(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", Object)
], Discussion.prototype, "stateProperties", null);
Discussion = __decorate([
    theme(css)
], Discussion);
export { Discussion };
